import api from './api';

export interface Supermarket {
  id: string;
  chain: string;
  name?: string;
  street: string;
  zipCode: string;
  city: string;
  country: string;
  addressHash: string;
  firstCreatedById: string;
  firstCreatedBy?: {
    id: string;
    name: string;
  };
  lastEditedById?: string;
  lastEditedBy?: {
    id: string;
    name: string;
  };
  lastEditedAt?: string;
  editCount: number;
  usageCount: number;
  isLocked: boolean;
  lockReason?: string;
  userLocked: boolean;
  zones: SupermarketZone[];
  createdAt: string;
  updatedAt: string;
  _count?: {
    lists: number;
    reports: number;
  };
}

export interface SupermarketZone {
  id: string;
  supermarketId: string;
  name: string;
  icon?: string;
  order: number;
  categoryMappings: CategoryMapping[];
}

export interface CategoryMapping {
  id: string;
  zoneId: string;
  categoryId: string;
  categoryName: string;
}

export interface CreateSupermarketData {
  chain: string;
  name?: string;
  street: string;
  zipCode: string;
  city: string;
  country?: string;
  zones: {
    name: string;
    icon?: string;
    categories: string[];
  }[];
}

export interface UpdateSupermarketData {
  chain?: string;
  name?: string;
  street?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  zones?: {
    name: string;
    icon?: string;
    categories: string[];
  }[];
  changeMessage?: string;
}

export interface SupermarketRevision {
  id: string;
  supermarketId: string;
  editedById: string;
  editedBy: {
    id: string;
    name: string;
  };
  snapshot: any;
  changeMessage?: string;
  createdAt: string;
}

export interface SupermarketReport {
  id: string;
  supermarketId: string;
  reportedById: string;
  reportedBy?: {
    id: string;
    name: string;
    email: string;
  };
  reason: 'INCORRECT_DATA' | 'OUTDATED' | 'DUPLICATE' | 'SPAM' | 'OTHER';
  details?: string;
  status: 'PENDING' | 'RESOLVED' | 'DISMISSED';
  resolvedById?: string;
  resolvedAt?: string;
  autoResolvedAt?: string;
  createdAt: string;
}

export interface SearchResult {
  exact: Supermarket | null;
  similar: Supermarket[];
}

class SupermarketService {
  // Get all supermarkets (user's + community)
  async getAll(): Promise<Supermarket[]> {
    const response = await api.get('/supermarkets');
    return response.data;
  }

  // Get single supermarket by ID
  async getById(id: string): Promise<Supermarket> {
    const response = await api.get(`/supermarkets/${id}`);
    return response.data;
  }

  // Search supermarkets by address
  async search(params: {
    chain: string;
    street: string;
    zipCode: string;
    city?: string;
    excludeId?: string;
  }): Promise<SearchResult> {
    const response = await api.get('/supermarkets/search', { params });
    return response.data;
  }

  // Create new supermarket
  async create(data: CreateSupermarketData): Promise<Supermarket> {
    const response = await api.post('/supermarkets', data);
    return response.data;
  }

  // Update supermarket
  async update(id: string, data: UpdateSupermarketData): Promise<Supermarket> {
    const response = await api.patch(`/supermarkets/${id}`, data);
    return response.data;
  }

  // Delete supermarket
  async delete(id: string): Promise<void> {
    await api.delete(`/supermarkets/${id}`);
  }

  // Toggle user lock (creator only)
  async toggleUserLock(id: string, userLocked: boolean): Promise<Supermarket> {
    const response = await api.patch(`/supermarkets/${id}/user-lock`, { userLocked });
    return response.data;
  }

  // Get revision history
  async getRevisions(id: string): Promise<SupermarketRevision[]> {
    const response = await api.get(`/supermarkets/${id}/revisions`);
    return response.data;
  }

  // Report supermarket
  async report(id: string, data: {
    reason: SupermarketReport['reason'];
    details?: string;
  }): Promise<SupermarketReport> {
    const response = await api.post(`/supermarkets/${id}/report`, data);
    return response.data;
  }

  // Link supermarket to list
  async linkToList(listId: string, supermarketId: string): Promise<void> {
    await api.patch(`/lists/${listId}/supermarket`, { supermarketId });
  }

  // Unlink supermarket from list
  async unlinkFromList(listId: string): Promise<void> {
    await api.delete(`/lists/${listId}/supermarket`);
  }

  // Helper: Get formatted address
  getFormattedAddress(supermarket: Supermarket): string {
    const parts = [
      supermarket.chain,
      supermarket.name,
      supermarket.street,
      `${supermarket.zipCode} ${supermarket.city}`,
    ].filter(Boolean);
    return parts.join(', ');
  }

  // Helper: Get short address
  getShortAddress(supermarket: Supermarket): string {
    return `${supermarket.chain}${supermarket.name ? ` ${supermarket.name}` : ''}, ${supermarket.street}`;
  }
}

export const supermarketService = new SupermarketService();
